import { CLASS_NAMES, EVENTS, MISC, SELECTORS } from '@bs/constants';
import { MessageBus } from '@bs/utils';

/**
 * A class for the GeoLocationPopup module
 */
export default class GeoLocationPopup extends MessageBus {
  /**
   * Constructor function of the GeoLocationPopup class. Must receive one argument, a DOM node representing the popup.
   * @param {HTMLElement} element - REQUIRED - the module's container
   */
  constructor(element) {
    super();
    this.element = element;
    this.KEY = SELECTORS.GEO_LOCATION_POPUP_SHOWN;

    // Initialize the view
    this.init();
  }

  /**
   * Initializes the view by calling functions to
   * create DOM references, setup event handlers and
   * then create the event listeners.
   * @returns {object} GeoLocationPopup A reference to the current instance of the class
   * @chainable
   */
  init() {
    this.cacheDomReferences().setupHandlers().enable();
    this.checkSessionStorage();
    return this;
  }

  /**
   * Cache DOM References
   *
   * Find all necessary DOM elements used in the view and cache them
   * @returns {object} GeoLocationPopup A reference to the current instance of the class
   * @chainable
   */
  cacheDomReferences() {
    this.popup = this.element.querySelector(
      `.${CLASS_NAMES.GEO_LOCATION_POPUP_BACKDROP}`
    );
    this.closeButtons = this.element.querySelectorAll(
      `.${CLASS_NAMES.GEO_LOCATION_POPUP_CLOSE}`
    );
    this.careButton = this.element.querySelector(`#${CLASS_NAMES.CARE_BUTTON}`);
    setTimeout(() => {
      this.lightbox = document.querySelector(`.${CLASS_NAMES.BASIC_LIGHTBOX}`);

      if (
        this.lightbox &&
        !sessionStorage.getItem(SELECTORS.GEO_LOCATION_POPUP_SHOWN)
      ) {
        this.lightbox.style.display = 'none';
      }
    }, 100);

    return this;
  }

  /**
   * Create event handlers to enable interaction with view
   * @returns {object} GeoLocationPopup A reference to the current instance of the class
   * @chainable
   */
  enable() {
    this.closeButtons.forEach((button) =>
      button.addEventListener(EVENTS.CLICK, this.handleCloseClick)
    );
    if (this.careButton) {
      this.careButton.addEventListener(
        EVENTS.CLICK,
        this.handleCareButtonClick
      );
    }

    return this;
  }

  /**
   * Bind event handlers with the proper context of `this`.
   * @returns {object} A reference to the current instance of the class
   * @chainable
   */
  setupHandlers() {
    this.handleCloseClick = this.handleCloseClick.bind(this);
    this.handleCareButtonClick = this.handleCareButtonClick.bind(this);
    return this;
  }

  /**
   * Checks session storage and hides the popup if necessary.
   */
  checkSessionStorage() {
    if (sessionStorage.getItem(this.KEY)) {
      this.hidePopup();
    }

    if (sessionStorage.getItem(SELECTORS.GEO_LOCATION_POPUP_SHOWN)) {
      if (this.lightbox) {
        this.lightbox.style.display = 'none';
      }
    }
  }

  /**
   * Hides the geolocation popup.
   */
  hidePopup() {
    if (this.element) {
      this.element.style.display = 'none';
    }
    sessionStorage.setItem(SELECTORS.GEO_LOCATION_POPUP_SHOWN, MISC.TRUE);
  }

  /**
   * Handles close button click event
   * @param {Event} event - the event object
   */
  handleCloseClick(event) {
    event.preventDefault();
    sessionStorage.setItem(this.KEY, MISC.TRUE);
    this.hidePopup();
  }

  /**
   * Handles "Stay on Care.org" button click event
   * @param {Event} event - the event object
   */
  handleCareButtonClick(event) {
    event.preventDefault();
    sessionStorage.setItem(this.KEY, MISC.TRUE);
    this.hidePopup();
  }
}
